import {
  FilterAction,
  FilterActions,
  FilterKeys,
  PriceFilter,
  VehicleFilterPricing,
} from "@modules/vehicle/types/vehicleFilter";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import Slider from "@common/components/form/Slider";
import { useRouter } from "next/router";
import MoneyInput from "@common/components/form/MoneyInput";

const inputDelayMs = 2000;

type Props = {
  onUpdate(filterAction: FilterAction): void;
  priceFilter: PriceFilter | undefined;
  pricing: VehicleFilterPricing;
};

const VehiclePriceFilter = ({ onUpdate, priceFilter, pricing }: Props) => {
  const { t } = useTranslation(["common"]);
  const router = useRouter();
  const locale = router.locale || "";

  const priceFilterState = priceFilter ? priceFilter.state : null;

  const [minPriceInputValue, setMinPriceInputValue] = useState<number>(
    priceFilterState !== null ? priceFilterState.minPrice : pricing.minPrice
  );
  const [maxPriceInputValue, setMaxPriceInputValue] = useState<number>(
    priceFilterState !== null ? priceFilterState.maxPrice : pricing.maxPrice
  );

  const [minPriceTimer, setMinPriceTimer] = useState<NodeJS.Timer>();
  const [maxPriceTimer, setMaxPriceTimer] = useState<NodeJS.Timer>();

  const updateSharedState = (minPrice: number, maxPrice: number) => {
    const isDefault =
      minPrice === pricing.minPrice && maxPrice === pricing.maxPrice;

    if (isDefault) {
      onUpdate({
        type: FilterActions.RemoveFilter,
        payload: FilterKeys.Price,
      });
    } else {
      onUpdate({
        type: FilterActions.AddPriceFilter,
        payload: {
          minPrice,
          maxPrice,
        },
      });
    }
  };

  useEffect(() => {
    if (priceFilterState !== null) {
      setMinPriceInputValue(priceFilterState.minPrice);
      setMaxPriceInputValue(priceFilterState.maxPrice);
    } else {
      setMinPriceInputValue(pricing.minPrice);
      setMaxPriceInputValue(pricing.maxPrice);
    }
  }, [priceFilterState, pricing.maxPrice, pricing.minPrice]);

  const onChangeMinPrice = (strippedValue: number) => {
    if (!Number.isNaN(strippedValue)) {
      setMinPriceInputValue(strippedValue);

      clearTimeout(minPriceTimer);

      const newTimer = setTimeout(() => {
        if (
          strippedValue <= maxPriceInputValue &&
          strippedValue >= pricing.minPrice
        ) {
          updateSharedState(strippedValue, maxPriceInputValue);
        } else {
          setMinPriceInputValue(priceFilterState?.minPrice ?? pricing.minPrice);
        }
      }, inputDelayMs);

      setMinPriceTimer(newTimer);
    }
  };

  const onChangeMaxPrice = (strippedValue: number) => {
    if (!Number.isNaN(strippedValue)) {
      setMaxPriceInputValue(strippedValue);

      clearTimeout(maxPriceTimer);

      const newTimer = setTimeout(() => {
        if (
          strippedValue >= minPriceInputValue &&
          strippedValue <= pricing.maxPrice
        ) {
          updateSharedState(minPriceInputValue, strippedValue);
        } else {
          setMaxPriceInputValue(priceFilterState?.maxPrice ?? pricing.maxPrice);
        }
      }, inputDelayMs);

      setMaxPriceTimer(newTimer);
    }
  };

  const onAfterSliderChange = (
    sliderChangeValue: number | readonly number[]
  ) => {
    const sliderChangeValueArray = sliderChangeValue as number[];
    const minPriceValue = sliderChangeValueArray[0];
    const maxPriceValue = sliderChangeValueArray[1];

    if (minPriceValue !== undefined && maxPriceValue !== undefined) {
      updateSharedState(minPriceValue, maxPriceValue);
    }
  };

  const onSliderChange = (sliderChangeValue: number | readonly number[]) => {
    const sliderChangeValueArray = sliderChangeValue as number[];
    const minPriceValue = sliderChangeValueArray[0];
    const maxPriceValue = sliderChangeValueArray[1];

    if (minPriceValue !== undefined && maxPriceValue !== undefined) {
      setMinPriceInputValue(minPriceValue);
      setMaxPriceInputValue(maxPriceValue);
    }
  };

  return (
    <div className="mt-7">
      <div className="flex gap-4">
        <div
          className={`w-full ${
            minPriceInputValue > pricing.minPrice ? "text-primary-bold" : ""
          }`}
        >
          <MoneyInput
            id="minimumVehiclePriceFilter"
            label={t("common:min")}
            onChange={onChangeMinPrice}
            value={minPriceInputValue}
            locale={locale}
          />
        </div>

        <div
          className={`w-full ${
            maxPriceInputValue < pricing.maxPrice ? "text-primary-bold" : ""
          }`}
        >
          <MoneyInput
            id="maximumVehiclePriceFilter"
            label={t("common:max")}
            onChange={onChangeMaxPrice}
            value={maxPriceInputValue}
            locale={locale}
          />
        </div>
      </div>

      <div className="pt-2">
        <Slider
          className="horizontal-slider"
          min={pricing.minPrice}
          max={pricing.maxPrice}
          value={[minPriceInputValue, maxPriceInputValue]}
          defaultValue={[pricing.minPrice, pricing.maxPrice]}
          onAfterChange={onAfterSliderChange}
          onChange={onSliderChange}
          step={1000}
          formatDisplayValue="money"
          locale={locale}
          ariaLabel="Vehicle Pricing Slider"
        />
      </div>
    </div>
  );
};

export default VehiclePriceFilter;
